import {
  TeButton,
  TeContainerSlim,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
  TeIcon,
  TeText,
} from '@topcon/tuicl-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { ServiceAgreementStatusEnum, ServiceAgreementTermsEnum } from '../../../api/generated/pm';
import { ServiceAgreementRowProps } from './ServiceAgreementRow';
import { ThemeColor } from '../../../providers/theme/theme.types';
import { ServiceAgreementExpiredEnum, ServiceAgreementStatus } from './ServiceAgreementTypes';

export const ACTION_BUTTON_MAX_WIDTH = 300;

type ColumnArgs = ServiceAgreementRowProps & {
  t: ReturnType<typeof useTranslation>['t'];
  hasSupportRole: boolean;
  isTablet?: boolean;
  isCompactTablet?: boolean;
};

export function buildColumns({
  sa,
  t,
  hasSupportRole,
  isTablet = false,
  isCompactTablet = false,
  onReview,
  onCancelRequest,
  onDeleteRequest,
  onSupportCustomer,
}: ColumnArgs): { key: string; render: () => React.ReactNode }[] {
  const cols: { key: string; render: () => React.ReactNode }[] = [];
  const status = sa.computedServiceAgreementStatus;
  const actionButtonStyle = isCompactTablet ? { maxWidth: ACTION_BUTTON_MAX_WIDTH } : undefined;

  const dateCell = (labelKey: string, value?: string) => (
    <TeContainerSlim column gap="xs" justifyContent="flex-start">
      <TeText weight={400} size={12} color="text.secondary">
        {t(labelKey)}
      </TeText>
      <TeText weight={500} size={14}>
        {value ? format(new Date(value), 'dd MMM yyyy') : '-'}
      </TeText>
    </TeContainerSlim>
  );

  const expiresInCell = (labelKey: string, date?: number) => (
    <TeContainerSlim column gap="xs" justifyContent="flex-start">
      <TeContainerSlim gap="xs2" alignItems="center">
        <TeText weight={400} size={12} color="text.secondary">
          {t(labelKey)}
        </TeText>
        <TeIcon name="info" size="s" color="icon.secondary" />
      </TeContainerSlim>
      <TeText weight={500} size={14}>
        {date ? date + ` ${t('days')}` : '-'}
      </TeText>
    </TeContainerSlim>
  );

  const moreMenu = (
    items: React.ReactNode,
    { standalone = false }: { standalone?: boolean } = {}
  ) => {
    const useOptionsButton = isTablet && standalone;

    return (
      <>
        <TeContextMenuControl contextId={`service-agreement-${sa.id}`}>
          {useOptionsButton ? (
            <TeButton
              variant="neutral"
              text={t('options')}
              postfixIcon="expand_more"
              size="s"
              grow
              style={actionButtonStyle}
            />
          ) : (
            <TeButton icon="more_vert" variant="neutral" size="s" />
          )}
        </TeContextMenuControl>
        <TeContextMenu contextId={`service-agreement-${sa.id}`} size="s">
          {items}
        </TeContextMenu>
      </>
    );
  };

  const actionsCell = (children: React.ReactNode) => (
    <TeContainerSlim
      justifyContent={isCompactTablet ? 'flex-end' : 'flex-start'}
      alignItems="center"
      gap="xs"
      fill={!isTablet}
      grow={isTablet}
      width={isTablet ? '100%' : undefined}
    >
      {children}
    </TeContainerSlim>
  );

  switch (status) {
    case ServiceAgreementStatusEnum.ACTIVE: {
      cols.push({
        key: 'activeSince',
        render: () => dateCell('activeSince', sa.activeSince ?? undefined),
      });
      cols.push({
        key: 'actions',
        render: () =>
          actionsCell(
            moreMenu(
              <>
                <TeContextMenuItem text={t('reviewAgreement')} teclick={() => onReview?.(sa)} />
                {hasSupportRole && (
                  <TeContextMenuItem
                    text={t('supportCustomer')}
                    teclick={() => onSupportCustomer?.(sa)}
                  />
                )}
                <TeContextMenuItem
                  text={t('terminateAgreement')}
                  textColor="text.error.primary"
                  teclick={() => onDeleteRequest?.(sa)}
                />
              </>,
              { standalone: true }
            )
          ),
      });
      break;
    }

    case ServiceAgreementStatusEnum.INVITED: {
      cols.push({
        key: 'requestedAt',
        render: () => dateCell('requestedAt', sa.createdAt ?? undefined),
      });
      cols.push({
        key: 'expiresIn',
        render: () => expiresInCell('expiresIn', sa.requestExpiresIn ?? undefined),
      });

      cols.push({
        key: 'actions',
        render: () =>
          actionsCell(
            <>
              <TeButton
                variant="neutral"
                text={t('review')}
                size="s"
                grow={isTablet}
                style={actionButtonStyle}
                teclick={() => onReview?.(sa)}
              />
            </>
          ),
      });

      break;
    }

    case ServiceAgreementStatusEnum.PENDING: {
      cols.push({
        key: 'requestedAt',
        render: () => dateCell('requestedAt', sa.createdAt ?? undefined),
      });
      cols.push({
        key: 'requestExpiresIn',
        render: () => expiresInCell('requestExpiresIn', sa.requestExpiresIn ?? undefined),
      });

      cols.push({
        key: 'actions',
        render: () =>
          actionsCell(
            moreMenu(
              <TeContextMenuItem
                text={t('cancelAgreementModal.title')}
                textColor="text.error.primary"
                teclick={() => onCancelRequest?.(sa)}
              />,
              { standalone: true }
            )
          ),
      });

      break;
    }

    case ServiceAgreementExpiredEnum.Expired: {
      cols.push({
        key: 'requestExpiresIn',
        render: () =>
          expiresInCell(
            'requestExpiredBy',
            sa.requestExpiresIn != null ? Math.abs(sa.requestExpiresIn) : undefined
          ),
      });
      cols.push({
        key: 'actions',
        render: () =>
          actionsCell(
            moreMenu(
              <TeContextMenuItem
                text={t('cancelAgreementModal.title')}
                textColor="text.error.primary"
                teclick={() => onCancelRequest?.(sa)}
              />,
              { standalone: true }
            )
          ),
      });
      break;
    }
    case ServiceAgreementStatusEnum.DECLINED: {
      cols.push({
        key: 'createdAt',
        render: () => dateCell('createdAt', sa.createdAt ?? undefined),
      });

      cols.push({
        key: 'actions',
        render: () =>
          actionsCell(
            moreMenu(
              <TeContextMenuItem
                text={t('cancelAgreementModal.title')}
                textColor="text.error.primary"
                teclick={() => onCancelRequest?.(sa)}
              />,
              { standalone: true }
            )
          ),
      });

      break;
    }

    default:
      break;
  }

  return cols;
}

interface StatusBadgeProps {
  text: string;
  bg: ThemeColor;
  borderColor: ThemeColor;
  textColor: ThemeColor;
}

export function getStatusBadge(
  status: ServiceAgreementStatus,
  t: ReturnType<typeof useTranslation>['t']
): StatusBadgeProps {
  switch (status) {
    case ServiceAgreementStatusEnum.ACTIVE:
      return {
        text: t('dealers.serviceAgreementStatus.active'),
        bg: 'background.success.secondary',
        borderColor: 'border.success.onBackground',
        textColor: 'text.success.onBackground',
      };
    case ServiceAgreementStatusEnum.PENDING:
      return {
        text: t('dealers.serviceAgreementStatus.pending'),
        bg: 'background.info.secondary',
        borderColor: 'border.info.onBackground',
        textColor: 'text.info.onBackground',
      };
    case ServiceAgreementStatusEnum.INVITED:
      return {
        text: t('dealers.serviceAgreementStatus.request'),
        bg: 'background.info.secondary',
        borderColor: 'border.info.onBackground',
        textColor: 'text.info.onBackground',
      };
    case ServiceAgreementStatusEnum.DECLINED:
      return {
        text: t('dealers.serviceAgreementStatus.declined'),
        bg: 'background.error.secondary',
        borderColor: 'border.error.onBackground',
        textColor: 'text.error.onBackground',
      };
    case ServiceAgreementExpiredEnum.Expired:
      return {
        text: t('dealers.serviceAgreementStatus.expired'),
        bg: 'background.secondary',
        borderColor: 'border.primary',
        textColor: 'text.primary',
      };
    default:
      return {
        text: '-',
        bg: 'background.secondary',
        borderColor: 'border.primary',
        textColor: 'text.primary',
      };
  }
}

export function getAgreementTypeText(
  agreementTerms: ServiceAgreementTermsEnum,
  t: (key: string) => string
): string {
  return agreementTerms === ServiceAgreementTermsEnum.FULL_SERVICE
    ? t('serviceAgreement.full_service')
    : t('serviceAgreement.limited_service');
}
